import {faCalculator, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import {clamp, range} from "lodash";
import React from "react";
import {IntlContext, IntlShape} from "react-intl";

import {ILocalizedText} from "@translate/models";
import {
    ENormalizationType,
    IChannelModel,
    ICycleDuration,
    ICycleModel,
} from "../../models";
import {ISopLimits} from "../models";

import RcaModal from "@/components/rcaCalc/RcaModal";
import {getCycleDurationWarning} from "@cent/sop/info/cycle-functions";
import Duration from "@shared/sop/Duration";
import Tooltip from "@toolbox/building-blocks/Tooltip";
import InlineSelect from "@toolbox/button-like/InlineSelect";
import NumberInput from "@toolbox/nativ-inputs/NumberInput";
import T, {intl2Str} from "@translate/T";

export function checkInvalidLightfactor(
    channels: Partial<IChannelModel>[],
    cycles: ICycleModel[],
) {
    const invalid =
        cycles.some((x) => x.lightFactor > 1) &&
        channels.some(
            (x) => x.normalizationType === ENormalizationType.EmptyCuvette,
        );
    if (invalid) {
        return (
            <T>
                If the light factor in a cycle exceeds 1, an optical filter
                without a cuvette is required for normalization. Please, change
                the normalization type or light factor.
            </T>
        );
    }

    return undefined;
}

interface ICycleRowProps {
    keyID: number;
    value: ICycleModel;
    channels: IChannelModel[];
    duration: ICycleDuration;

    localMaxProfiles: number;
    limits: ISopLimits;
    canRemove: boolean;

    onRemoved(value: ICycleModel): void;
    onChange(updated: ICycleModel, old: ICycleModel): void;
}

class CycleRow extends React.PureComponent<ICycleRowProps> {
    private readonly ref = React.createRef<RcaModal>();

    private get isDurationValid() {
        const {duration, limits} = this.props;
        return duration.endTime <= limits.maxDuration;
    }

    private get isGlobalProfilValid() {
        const {localMaxProfiles, limits} = this.props;
        return localMaxProfiles <= limits.maxGlobalProfiles;
    }

    public componentDidUpdate(prev: ICycleRowProps) {
        const {limits, onChange, value} = this.props;
        if (prev.limits === limits) {
            return;
        }

        const profiles = clamp(value.profiles, 1, limits.maxProfiles);
        const level = clamp(value.level, limits.level[0], limits.level[1]);
        const interval = clamp(
            value.interval,
            limits.interval[0],
            limits.interval[1],
        );
        const lightFactor = clamp(
            value.lightFactor,
            limits.lightFactor[0],
            limits.lightFactor[1],
        );
        const temperature = clamp(
            value.temperature,
            limits.temperature[0],
            limits.temperature[1],
        );

        const hasChange =
            profiles !== value.profiles ||
            level !== value.level ||
            interval !== value.interval ||
            lightFactor !== value.lightFactor ||
            temperature !== value.temperature;
        if (!hasChange) {
            return;
        }

        onChange({interval, level, lightFactor, profiles, temperature}, value);
    }

    @boundMethod
    public updateProfiles(profiles: number) {
        const {value, onChange} = this.props;
        onChange({...value, profiles}, value);
    }

    @boundMethod
    public updateInterval(interval: number) {
        const {value, onChange} = this.props;
        onChange({...value, interval}, value);
    }

    @boundMethod
    public updateSpeed(level: number) {
        const {value, onChange} = this.props;
        onChange({...value, level}, value);
    }

    @boundMethod
    public updateLightFactor(lightFactor: number) {
        const {value, onChange} = this.props;
        onChange({...value, lightFactor}, value);
    }

    @boundMethod
    public updateTemperature(temperature: number) {
        const {value, onChange} = this.props;
        onChange({...value, temperature}, value);
    }

    @boundMethod
    public removeCycle(e: React.SyntheticEvent) {
        e.preventDefault();

        const {canRemove, onRemoved, value} = this.props;
        if (canRemove) {
            onRemoved(value);
        }
    }

    @boundMethod
    public onCalcClick() {
        this.ref.current?.open();
    }

    public render() {
        const {value, duration, limits} = this.props;
        const invalidMessage: ILocalizedText | undefined =
            duration.min > 1
                ? (intl) =>
                      intl2Str(
                          intl,
                          "Measurement starts with a profile, interval is between profiles, for first cycle additional starting profile needed, one interval less.",
                      )
                : !this.isDurationValid
                ? (intl) => intl2Str(intl, "SOP duration is too long.")
                : !this.isGlobalProfilValid
                ? (intl) => intl2Str(intl, "Too many profiles in SOP.")
                : undefined;

        return (
            <tr>
                <td className="align-middle">
                    <Duration seconds={duration.startTime} />
                </td>
                <td>
                    <NumberInput
                        id={"profiles-" + this.props.keyID}
                        decimals={0}
                        invalid={
                            !this.isDurationValid || !this.isGlobalProfilValid
                        }
                        max={limits.maxProfiles}
                        min={duration.min}
                        step={10}
                        title={invalidMessage}
                        value={value.profiles}
                        onChange={this.updateProfiles}
                    />
                </td>
                <td>
                    <NumberInput
                        id={"interval-" + this.props.keyID}
                        decimals={0}
                        invalid={!this.isDurationValid}
                        max={limits.interval[1]}
                        min={limits.interval[0]}
                        step={10}
                        title={invalidMessage}
                        value={value.interval}
                        onChange={this.updateInterval}
                    />
                </td>
                {this.renderSpeed()}
                {this.renderLightFactor()}
                {this.renderTemperature()}
                <IntlContext.Consumer children={this.renderDuration} />
                {this.renderButtons()}
            </tr>
        );
    }

    private renderSpeed() {
        const {value, limits} = this.props;
        if (!limits.hasRotation) {
            return null;
        }

        return (
            <td>
                <NumberInput
                    id={"speed-" + this.props.keyID}
                    step={100}
                    decimals={0}
                    value={value.level}
                    min={limits.level[0]}
                    max={limits.level[1]}
                    onChange={this.updateSpeed}
                />
            </td>
        );
    }

    private renderLightFactor() {
        const {value, channels, limits} = this.props;
        if (!limits.hasLightFactor) {
            return null;
        }

        const tooltip = checkInvalidLightfactor(channels, [value]);

        return (
            <td>
                <Tooltip content={tooltip}>
                    <InlineSelect<number>
                        idSuffix={"lightfactor-" + this.props.keyID}
                        invalid={!!tooltip}
                        selected={value.lightFactor}
                        onSelected={this.updateLightFactor}
                        values={range(
                            limits.lightFactor[0],
                            limits.lightFactor[1] + 0.01,
                            0.25,
                        )}
                    />
                </Tooltip>
            </td>
        );
    }

    private renderTemperature() {
        const {value, limits} = this.props;
        if (!limits.hasTemp || !limits.temperatureRamp) {
            return null;
        }

        return (
            <td>
                <NumberInput
                    id={"temperature-" + this.props.keyID}
                    step={1}
                    decimals={0}
                    value={value.temperature}
                    min={limits.temperature[0]}
                    max={limits.temperature[1]}
                    onChange={this.updateTemperature}
                />
            </td>
        );
    }

    @boundMethod
    private renderDuration(intl: IntlShape) {
        const {value, duration, limits} = this.props;

        const {title, className} = getCycleDurationWarning(
            intl,
            duration,
            limits,
            value.interval,
        );

        return (
            <td className={className} title={title}>
                <Duration seconds={duration.duration} />
            </td>
        );
    }

    private renderButtons() {
        const {limits, value} = this.props;
        if (!limits.hasRotation) {
            return <td>{this.renderRemoveButton()}</td>;
        }

        return (
            <td>
                <div className="btn-group">
                    <RcaModal
                        ref={this.ref}
                        speed={value.level}
                        min={limits.level[0]}
                        max={limits.level[1]}
                        onChange={this.updateSpeed}
                    />
                    <button
                        type="button"
                        className="btn btn-info"
                        onClick={this.onCalcClick}
                    >
                        <FontAwesomeIcon
                            icon={faCalculator}
                            fixedWidth={true}
                        />
                    </button>
                    {this.renderRemoveButton()}
                </div>
            </td>
        );
    }

    private renderRemoveButton() {
        const {canRemove, keyID} = this.props;

        return (
            <button
                type="button"
                id={`remove-cycle-${keyID}`}
                className="btn btn-danger"
                disabled={!canRemove}
                onClick={this.removeCycle}
            >
                <FontAwesomeIcon icon={faTrash} fixedWidth={true} />
            </button>
        );
    }
}

export default CycleRow;
